// ─────────────────────────────────────────────────────────────────────────────
// Promo Attribution Service
// Atribuição de promoção (banner) ao longo do funil: view → click → purchase.
//
// Regras (alinhadas à validação GA4 do cliente):
//  1. Só CLIQUE atribui. view_promotion nunca cria atribuição.
//  2. O clique vale por uma janela curta (CLICK_TTL_MS). Fora dela, itens
//     adicionados ao carrinho não são atribuídos à promoção.
//  3. A atribuição é POR ITEM: o vínculo nasce no add_to_cart e só os itens
//     vinculados levam promotion_id/creative_slot/creative_name no purchase.
//     Sem isso todo item de toda compra da sessão herdava a última promoção e
//     "items purchased in promotion" estourava acima de "items clicked".
//  4. O vínculo é consumido no purchase — não reaparece na compra seguinte.
//
// Memória da LOJA e do CLUBE são independentes (chaves diferentes).
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Banner } from '@app/models/banner';
import { splitBannerImageUrl } from './analytics.config';
import { PURCHASE_ORIGIN_CLUBE, PurchaseOrigin } from './analytics.types';

export interface PromoAttribution {
  promotion_id: string;
  /** Domínio da imagem do banner (primeira metade da URL do criativo). */
  promotion_name: string;
  creative_slot: string;
  /** Path da imagem do banner (segunda metade da URL do criativo). */
  creative_name: string;
  /** URL completa do criativo, mantida para as dimensões já registradas. */
  promo_img?: string;
  promo_dest?: string;
  origin_screen: string;
  timestamp: number;
}

/** Vínculo item→promoção criado no add_to_cart. */
interface PromoItemBinding {
  promo: PromoAttribution;
  bound_at: number;
}

const STORAGE_KEY_LOJA = 'ga4_promo_loja';
const STORAGE_KEY_CLUBE = 'ga4_promo_clube';
const STORAGE_KEY_BINDINGS = 'ga4_promo_items';

/** Janela em que o clique no banner ainda atribui itens adicionados ao carrinho. */
const CLICK_TTL_MS = 30 * 60 * 1000;
/**
 * Tipos de banner que levam para dentro da loja (detalhe do produto / catálogo).
 * pdf, url, link e youtube tiram o usuário do fluxo de compra — o clique conta
 * como select_promotion, mas não atribui a compra que vier depois.
 */
const SHOPPABLE_BANNER_TYPES: ReadonlyArray<Banner['type']> = ['promotion', 'product'];
/** Validade do vínculo item→promoção (carrinho é server-side e sobrevive ao app). */
const BINDING_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Teto de vínculos guardados — evita crescer sem limite no localStorage. */
const MAX_BINDINGS = 400;

@Injectable({ providedIn: 'root' })
export class PromoAttributionService {

  // ════════════════════════════════════════════════════════════════════════════
  // Clique no banner (último clique por origem)
  // ════════════════════════════════════════════════════════════════════════════

  /** Registra o clique em banner da Loja. */
  setPromoAttribution(banner: Banner, originScreen: string, slotIndex?: number): void {
    this.writeClick(STORAGE_KEY_LOJA, banner, originScreen, slotIndex);
  }

  /** Registra o clique em banner do Clube. */
  setPromoAttributionClube(banner: Banner, originScreen: string, slotIndex?: number): void {
    this.writeClick(STORAGE_KEY_CLUBE, banner, originScreen, slotIndex);
  }

  /** Último clique válido da Loja (null se expirado). */
  getPromoAttribution(): PromoAttribution | null {
    return this.readClick(STORAGE_KEY_LOJA);
  }

  /** Último clique válido do Clube (null se expirado). */
  getPromoAttributionClube(): PromoAttribution | null {
    return this.readClick(STORAGE_KEY_CLUBE);
  }

  /** Clique ativo da origem, com fallback Clube → Loja (banner da home leva ao Clube). */
  getActivePromo(origin: PurchaseOrigin): PromoAttribution | null {
    return origin === PURCHASE_ORIGIN_CLUBE
      ? (this.getPromoAttributionClube() ?? this.getPromoAttribution())
      : this.getPromoAttribution();
  }

  /** Descarta o clique ativo da origem (após o purchase). */
  clearActivePromo(origin: PurchaseOrigin): void {
    this.removeItem(
      sessionStorage,
      origin === PURCHASE_ORIGIN_CLUBE ? STORAGE_KEY_CLUBE : STORAGE_KEY_LOJA,
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // Vínculo item → promoção (criado no add_to_cart)
  // ════════════════════════════════════════════════════════════════════════════

  /**
   * Vincula um produto à promoção clicada, se houver clique dentro da janela.
   * `keys` são todos os identificadores do produto (sku, id, code, reference…):
   * o carrinho devolve `code`/`reference` e o catálogo `sku`/`id`, então gravar
   * em todas as chaves garante que o purchase reencontre o vínculo.
   */
  bindItemToActivePromo(keys: string[], origin: PurchaseOrigin): PromoAttribution | null {
    const promo = this.getActivePromo(origin);
    const validKeys = this.normalizeKeys(keys);
    if (!promo || !validKeys.length) return null;

    const bindings = this.readBindings();
    const binding: PromoItemBinding = { promo, bound_at: Date.now() };
    validKeys.forEach((key) => bindings[this.bindingKey(key, origin)] = binding);
    this.writeBindings(bindings);
    return promo;
  }

  /** Promoção vinculada ao item, se houver vínculo válido. */
  getItemPromo(keys: string[], origin: PurchaseOrigin): PromoAttribution | null {
    const validKeys = this.normalizeKeys(keys);
    if (!validKeys.length) return null;

    const bindings = this.readBindings();
    for (const key of validKeys) {
      const binding = bindings[this.bindingKey(key, origin)];
      if (binding && !this.isExpired(binding.bound_at, BINDING_TTL_MS)) {
        return binding.promo;
      }
    }
    return null;
  }

  /** Consome os vínculos dos itens comprados — a compra encerra a atribuição. */
  consumeItemPromos(keysByItem: string[][], origin: PurchaseOrigin): void {
    const bindings = this.readBindings();
    let changed = false;
    keysByItem.forEach((keys) => {
      this.normalizeKeys(keys).forEach((key) => {
        const storageKey = this.bindingKey(key, origin);
        if (storageKey in bindings) {
          delete bindings[storageKey];
          changed = true;
        }
      });
    });
    if (changed) this.writeBindings(bindings);
  }

  // ════════════════════════════════════════════════════════════════════════════
  // Internals
  // ════════════════════════════════════════════════════════════════════════════

  private writeClick(storageKey: string, banner: Banner, originScreen: string, slotIndex?: number): void {
    if (!SHOPPABLE_BANNER_TYPES.includes(banner?.type)) return;

    const image = banner.image ?? '';
    // Mesma divisão domínio/path do view_promotion — o purchase precisa remontar
    // a mesma URL de criativo que o clique registrou.
    const { origin, path } = splitBannerImageUrl(image);
    const attr: PromoAttribution = {
      promotion_id: String(banner.id ?? ''),
      promotion_name: origin,
      creative_slot: `slot_${slotIndex ?? 0}`,
      creative_name: path,
      promo_img: image || undefined,
      promo_dest: banner.link ?? undefined,
      origin_screen: originScreen,
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(attr));
    } catch { /* silencioso */ }
  }

  private readClick(storageKey: string): PromoAttribution | null {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const attr = JSON.parse(raw) as PromoAttribution;
      if (this.isExpired(attr?.timestamp, CLICK_TTL_MS)) {
        this.removeItem(sessionStorage, storageKey);
        return null;
      }
      return this.normalizeAttribution(attr);
    } catch {
      return null;
    }
  }

  private readBindings(): Record<string, PromoItemBinding> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_BINDINGS);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, PromoItemBinding>;
      const out: Record<string, PromoItemBinding> = {};
      for (const [key, binding] of Object.entries(parsed ?? {})) {
        if (binding?.promo && !this.isExpired(binding.bound_at, BINDING_TTL_MS)) {
          out[key] = { ...binding, promo: this.normalizeAttribution(binding.promo) };
        }
      }
      return out;
    } catch {
      return {};
    }
  }

  private writeBindings(bindings: Record<string, PromoItemBinding>): void {
    try {
      const entries = Object.entries(bindings);
      // Acima do teto, descarta os vínculos mais antigos.
      const trimmed = entries.length > MAX_BINDINGS
        ? entries.sort((a, b) => b[1].bound_at - a[1].bound_at).slice(0, MAX_BINDINGS)
        : entries;
      localStorage.setItem(STORAGE_KEY_BINDINGS, JSON.stringify(Object.fromEntries(trimmed)));
    } catch { /* silencioso */ }
  }

  /**
   * Converte atribuições gravadas no formato antigo (creative_name com a URL
   * inteira, promotion_name com o tipo do banner). Os vínculos vivem até 30
   * dias, então sem isso o purchase seguiria mandando o formato antigo por
   * semanas. É no-op no formato novo — o path não tem domínio para separar.
   */
  private normalizeAttribution(attr: PromoAttribution): PromoAttribution {
    const { origin, path } = splitBannerImageUrl(attr?.creative_name);
    return origin ? { ...attr, promotion_name: origin, creative_name: path } : attr;
  }

  private bindingKey(itemKey: string, origin: PurchaseOrigin): string {
    return `${origin}:${itemKey}`;
  }

  private normalizeKeys(keys: string[]): string[] {
    return Array.from(new Set((keys ?? []).filter((key) => !!key).map(String)));
  }

  private isExpired(timestamp: number | undefined, ttlMs: number): boolean {
    if (!Number.isFinite(timestamp as number)) return true;
    return Date.now() - (timestamp as number) > ttlMs;
  }

  private removeItem(storage: Storage, key: string): void {
    try {
      storage.removeItem(key);
    } catch { /* silencioso */ }
  }
}
